import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { FiArrowDown, FiArrowUp, FiMoreHorizontal, FiRepeat } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import type { Transaction } from "@/hooks/useTransactions";
import { flowArrow, formatCurrency, formatFlowAmount, formatSignedCurrency } from "@/utils/formatters";
import type { TransactionActivityItem } from "./TransactionActivityList";

interface TransactionDayGroup {
  key: string;
  label: string;
  total: number;
  items: TransactionActivityItem[];
}

interface TransactionDataTableProps {
  groups: TransactionDayGroup[];
  filteredCount: number;
  visibleCount: number;
  selectedTransactionIds: number[];
  isSelectionMode: boolean;
  isDuplicating: boolean;
  onTransactionClick: (transaction: Transaction) => void;
  onDuplicate: (transaction: Transaction) => Promise<void>;
  onEdit: (transaction: Transaction) => void;
  onDelete: (transactionId: number) => Promise<void>;
  onViewMore: () => void;
}

interface TransactionRowActionsProps {
  transaction: Transaction;
  name: string;
  isDuplicating: boolean;
  onDuplicate: (transaction: Transaction) => Promise<void>;
  onEdit: (transaction: Transaction) => void;
  onDelete: (transactionId: number) => Promise<void>;
}

const TransactionRowActions = ({
  transaction,
  name,
  isDuplicating,
  onDuplicate,
  onEdit,
  onDelete,
}: TransactionRowActionsProps) => {
  const { t } = useTranslation(["common", "transactions"]);
  const menuId = useId();
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  const updatePosition = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const menuHeight = 3 * 44 + 16;
    const top = rect.bottom + menuHeight + 8 > window.innerHeight
      ? Math.max(8, rect.top - menuHeight - 8)
      : rect.bottom + 8;
    setPosition({
      top,
      left: Math.max(8, Math.min(window.innerWidth - 208, rect.right - 200)),
    });
  };

  useEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }

    updatePosition();
    const closeOutside = (event: PointerEvent) => {
      if (!triggerRef.current?.contains(event.target as Node) && !menuRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener("resize", updatePosition);
    document.addEventListener("scroll", updatePosition, true);
    document.addEventListener("pointerdown", closeOutside);
    return () => {
      window.removeEventListener("resize", updatePosition);
      document.removeEventListener("scroll", updatePosition, true);
      document.removeEventListener("pointerdown", closeOutside);
    };
  }, [open]);

  useEffect(() => {
    if (open && position) {
      menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]:not(:disabled)')?.focus();
    }
  }, [open, position]);

  const closeAndRestore = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  const handleMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const items = Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not(:disabled)') ?? []);
    const currentIndex = items.indexOf(document.activeElement as HTMLElement);
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      closeAndRestore();
    } else if ((event.key === "ArrowDown" || event.key === "ArrowUp") && items.length > 0) {
      event.preventDefault();
      const delta = event.key === "ArrowDown" ? 1 : -1;
      items[(currentIndex + delta + items.length) % items.length]?.focus();
    } else if (event.key === "Home" || event.key === "End") {
      event.preventDefault();
      (event.key === "Home" ? items[0] : items[items.length - 1])?.focus();
    }
  };

  const runAction = (action: () => void) => {
    closeAndRestore();
    action();
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label={t("transactions.rowActions", { name, defaultValue: "Actions for {{name}}" })}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((current) => !current)}
        className="app-focus-ring inline-flex h-11 w-11 items-center justify-center rounded-full text-[var(--ds-ink)] hover:bg-[var(--ds-line)]"
      >
        <FiMoreHorizontal className="h-5 w-5" aria-hidden="true" />
      </button>
      {open && position
        ? createPortal(
            <div
              ref={menuRef}
              id={menuId}
              role="menu"
              aria-label={t("transactions.rowActions", { name, defaultValue: "Actions for {{name}}" })}
              tabIndex={-1}
              onKeyDown={handleMenuKeyDown}
              className="fixed z-[110] min-w-48 rounded-[var(--ds-radius-md)] border border-[var(--ds-line)] bg-[var(--ds-surface-raised)] p-2 text-[var(--ds-ink)] shadow-[var(--ds-shadow-2)]"
              style={position}
            >
              <button
                type="button"
                role="menuitem"
                disabled={isDuplicating}
                onClick={() => runAction(() => void onDuplicate(transaction))}
                className="app-focus-ring flex h-11 w-full items-center gap-3 rounded-[var(--ds-radius-sm)] px-3 text-left text-sm hover:bg-[var(--ds-line)] disabled:opacity-50"
              >{t("common:actions.duplicate", { defaultValue: "Duplicate" })}</button>
              <button
                type="button"
                role="menuitem"
                onClick={() => runAction(() => onEdit(transaction))}
                className="app-focus-ring flex h-11 w-full items-center gap-3 rounded-[var(--ds-radius-sm)] px-3 text-left text-sm hover:bg-[var(--ds-line)]"
              >{t("common:actions.edit", { defaultValue: "Edit" })}</button>
              <button
                type="button"
                role="menuitem"
                onClick={() => runAction(() => void onDelete(transaction.id))}
                className="app-focus-ring flex h-11 w-full items-center gap-3 rounded-[var(--ds-radius-sm)] px-3 text-left text-sm text-[var(--ds-negative)] hover:bg-[var(--ds-line)]"
              >{t("common:actions.delete", { defaultValue: "Delete" })}</button>
            </div>,
            document.body,
          )
        : null}
    </>
  );
};

const amountLabel = (item: TransactionActivityItem) => {
  if (item.direction === "expense" || item.direction === "income")
    return formatFlowAmount(item.transaction.amount, item.direction, { omitZeroSign: true });
  return `${flowArrow("transfer")} ${formatCurrency(item.transaction.amount)}`;
};

export const TransactionDataTable = ({
  groups,
  filteredCount,
  visibleCount,
  selectedTransactionIds,
  isSelectionMode,
  isDuplicating,
  onTransactionClick,
  onDuplicate,
  onEdit,
  onDelete,
  onViewMore,
}: TransactionDataTableProps) => {
  const { t } = useTranslation(["common", "transactions"]);
  const allDisplayedItems = groups.flatMap((group) => group.items);
  const selectedCount = allDisplayedItems.filter((item) => selectedTransactionIds.includes(item.transaction.id)).length;

  return (
    <div className="hidden min-[1024px]:block">
      {isSelectionMode && selectedCount > 0 ? (
        <div className="mb-3 text-sm text-[var(--ds-ink-muted)]" aria-live="polite">
          <span>{t("transactions.selectedCount", { count: selectedCount, defaultValue: "{{count}} selected" })}</span>
        </div>
      ) : null}
      <div className="transactions-table-shell overflow-x-auto rounded-[var(--ds-radius-lg)] bg-[var(--ds-surface-raised)]">
        <table className="transactions-table w-full min-w-[54rem] border-separate [border-spacing:0_8px] text-left text-[var(--ds-ink)]">
          <thead className="sticky top-0 z-10">
            <tr className="border-b border-[var(--ds-line)] text-[11px] font-medium uppercase tracking-[0.06em] text-[var(--ds-ink-muted)]">
              {isSelectionMode ? (
                <th scope="col" className="w-12 px-3 py-4">
                  <span className="sr-only">{t("common.actions.select", { defaultValue: "Select" })}</span>
                </th>
              ) : null}
              <th scope="col" className="px-3 py-4">{t("transactions.tableMovement", { defaultValue: "Movement" })}</th>
              <th scope="col" className="px-3 py-4">{t("common.labels.category", { defaultValue: "Category" })}</th>
              <th scope="col" className="px-3 py-4">{t("common.labels.tag", { defaultValue: "Tag" })}</th>
              <th scope="col" className="px-3 py-4">{t("common.labels.account", { defaultValue: "Account" })}</th>
              <th scope="col" className="px-3 py-4 text-right">{t("transactions.tableAmount", { defaultValue: "Amount" })}</th>
              <th scope="col" className="w-14 px-2 py-4 text-right"><span className="sr-only">{t("transactions.tableActions", { defaultValue: "Actions" })}</span></th>
            </tr>
          </thead>
          {groups.map((group) => (
            <tbody key={group.key}>
              <tr className="border-b border-[var(--ds-line)] bg-[var(--ds-surface)] text-sm font-medium text-[var(--ds-ink-muted)]">
                <th scope="rowgroup" colSpan={isSelectionMode ? 7 : 6} className="px-4 py-3 text-left">
                  <span>{group.label}</span>
                  <span className="ml-3 font-normal tabular-nums">{formatSignedCurrency(group.total)}</span>
                </th>
              </tr>
              {group.items.map((item) => {
                const isSelected = selectedTransactionIds.includes(item.transaction.id);
                const DirectionIcon = item.direction === "income" ? FiArrowUp : item.direction === "expense" ? FiArrowDown : FiRepeat;
                const tone = item.direction === "income" ? "bg-[var(--ds-card-lime)]" : item.direction === "expense" ? "bg-[var(--ds-card-mist)]" : "bg-[var(--ds-card-lilac)]";
                const amountTone = item.direction === "income" ? "text-[var(--ds-positive)]" : item.direction === "expense" ? "text-[var(--ds-negative)]" : "text-[var(--ds-ink)]";

                return (
                  <tr
                    key={item.transaction.id}
                    className={`transactions-entry-row h-16 border-b border-[var(--ds-line)] text-sm last:border-0 hover:bg-[var(--ds-line)] ${isSelected ? "bg-[color-mix(in_srgb,var(--ds-card-sun)_32%,var(--ds-surface-raised))]" : ""}`}
                  >
                    {isSelectionMode ? (
                      <td className="px-3 py-2">
                        <label className="app-focus-ring inline-flex h-11 w-11 items-center justify-center rounded-full">
                          <span className="sr-only">{t("transactions.selectMovement", { name: item.transaction.name, defaultValue: "Select {{name}}" })}</span>
                          <input
                            type="checkbox"
                            aria-label={t("transactions.selectMovement", { name: item.transaction.name, defaultValue: "Select {{name}}" })}
                            checked={isSelected}
                            onChange={() => onTransactionClick(item.transaction)}
                            className="app-focus-ring h-5 w-5 accent-[var(--ds-ink)]"
                          />
                        </label>
                      </td>
                    ) : null}
                    <td className="min-w-56 px-3 py-2">
                      <div className="flex min-w-0 items-center gap-3">
                        <span aria-hidden="true" className={`inline-flex h-11 w-11 flex-none items-center justify-center rounded-full text-[var(--ds-on-card)] ${tone}`}>
                          <DirectionIcon className="h-5 w-5" />
                        </span>
                        <span className="min-w-0">
                          <button
                            type="button"
                            onClick={() => onTransactionClick(item.transaction)}
                            aria-label={isSelectionMode
                              ? t("transactions.selectMovement", { name: item.transaction.name, defaultValue: "Select {{name}}" })
                              : t("transactions.openMovement", { name: item.transaction.name, defaultValue: "View or edit {{name}}" })}
                            className="transactions-movement-name app-focus-ring block max-w-[18rem] truncate rounded-sm text-left text-[var(--ds-ink)] underline-offset-2 hover:underline"
                          >{item.transaction.name}</button>
                          {item.transaction.description?.trim() ? <span className="transactions-description block max-w-[18rem] truncate text-[var(--ds-ink-muted)]">{item.transaction.description}</span> : null}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      {item.categoryName ? <span className="transactions-category-chip">{item.categoryName}</span> : <span className="text-[var(--ds-ink-muted)]">—</span>}
                    </td>
                    <td className="px-3 py-2">
                      {item.tagName ? <span className="transactions-tag-chip">{item.tagName}</span> : <span className="text-[var(--ds-ink-muted)]">—</span>}
                    </td>
                    <td className="transactions-account max-w-40 truncate px-3 py-2 text-[var(--ds-ink)]">{item.accountName}</td>
                    <td className={`transactions-amount whitespace-nowrap px-3 py-2 text-right tabular-nums ${amountTone}`}>{amountLabel(item)}</td>
                    <td className="px-2 py-2 text-right">
                      <TransactionRowActions
                        transaction={item.transaction}
                        name={item.transaction.name}
                        isDuplicating={isDuplicating}
                        onDuplicate={onDuplicate}
                        onEdit={onEdit}
                        onDelete={onDelete}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          ))}
        </table>
      </div>
      {filteredCount > visibleCount ? (
        <div className="mt-3 flex justify-end pb-8">
          <button type="button" onClick={onViewMore} className="app-focus-ring inline-flex min-h-11 items-center rounded-full px-4 text-sm font-medium text-[var(--ds-ink)] underline underline-offset-4 hover:bg-[var(--ds-line)]">
            {t("transactions.viewMore", { defaultValue: "See more movements" })} <span aria-hidden="true" className="ml-1">›</span>
          </button>
        </div>
      ) : null}
    </div>
  );
};
